"use client";
import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/supabase/client";

import { animate, createScope, createSpring } from "animejs";
import { useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";

import { UserContext } from "@/components/providers/userProvider";

import Pfp from "@/components/generic/pfp";

export default function Navbar({ theme: initialTheme }) {
    const root = useRef(null);
    const scope = useRef(null);

    useEffect(() => {
        scope.current = createScope({ root }).add((scope) => {
            animate(".logo", {
                scale: [
                    { to: 1.25, ease: "inOut(3)", duration: 200 },
                    { to: 1, ease: createSpring({ stiffness: 300 }) },
                ],
                loop: true,
                loopDelay: 250,
            });
        });

        return () => scope.current.revert();
    }, []);

    const { theme, setTheme } = useTheme();

    const handleThemeToggle = () => {
        const newTheme = theme === "dark" ? "light" : "dark";
        document.documentElement.classList.add("theme-transition");
        document
            .startViewTransition(() => {
                setTheme(newTheme);
                console.log("Theme changed to", newTheme);
            })
            .finished.finally(() => {
                document.documentElement.classList.remove("theme-transition");
            });
    };

    const { setAuthModalOpen, user } = UserContext();

    const UserSection = () => {
        if (user) {
            return (
                <div className="flex items-center gap-2">
                    <Pfp className={`w-8 h-8`} href={user.pfp} username={user.username} />
                    <Link href={`/user/${user.username}`}>{user.username}</Link>
                    <Button
                        variant="outline"
                        onClick={async () => {
                            const supabase = createClient();
                            await supabase.auth.signOut();
                        }}
                    >
                        Sign Out
                    </Button>
                </div>
            );
        }

        return <Button onClick={() => setAuthModalOpen(true)}>Sign In</Button>;
    };

    return (
        <NavigationMenu ref={root} className="flex justify-between w-full p-4 border-b">
            <div className="w-full">
                <NavigationMenuList className="w-full">
                    <NavigationMenuItem>
                        <NavigationMenuLink asChild>
                            <Link href="/" className={`logo ${navigationMenuTriggerStyle()} font-bold`} style={{ fontSize: "1.25rem" }}>
                                mmo.markets
                            </Link>
                        </NavigationMenuLink>
                    </NavigationMenuItem>

                    <NavigationMenuItem className="flex items-center justify-end flex-1 px-4">
                        <Button className={`rounded-full`} variant="outline" onClick={handleThemeToggle}>
                            {theme === "dark" ? <Moon className="size-3" /> : <Sun className="size-3" />}
                        </Button>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                        <UserSection />
                    </NavigationMenuItem>
                </NavigationMenuList>
            </div>
        </NavigationMenu>
    );
}
